import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { ReactComponent as FolderBrainIcon } from './folderBrainIcon.svg';
import apiService from '../../services/apiService';

// Item B: "Tell the AI what it needs to know" — durable, tagged business-context notes
// the forecasting agents read before they forecast. Reused in two places:
//   • the input workspace (entity-wide capture, near the operating bar), and
//   • OP_03T next to a GL group (compact, pre-tagged to that group).
//
// Edits never overwrite: the server supersedes with a new version. Notes are tagged
// (horizon, gl_group, expense_type, free category) and filterable.

const EXPENSE_TYPES = [
  { value: '', label: 'No expense tag' },
  { value: 'non_recurring', label: 'Non-recurring' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'one_time', label: 'One-time' }
];

const EMPTY_DRAFT = {
  body: '',
  horizon: 'durable',
  glGroup: '',
  expenseType: '',
  category: ''
};

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (error) {
    return iso.slice(0, 10);
  }
};

const ForecastContextNotes = ({
  scope, author, glGroup = '', disabled = false, compact = false,
  title, subtitle
}) => {
  const cycleCode = scope?.cycleCode || 'FY2027_BUDGET';
  const planningEntity = scope?.planningEntity || null;
  const division = scope?.division || null;
  const market = scope?.market || null;

  const [notes, setNotes] = useState([]);
  const [loadState, setLoadState] = useState('idle');
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT, glGroup });
  const [busy, setBusy] = useState(false);
  const [glOptions, setGlOptions] = useState([]); // [{ rollup, glGroup }] from the comparison GL set

  const load = useCallback(() => {
    if (!planningEntity) { setNotes([]); return; }
    setLoadState('loading');
    apiService
      .fetchContextNotes({ cycleCode, entity: planningEntity, currentOnly: 'true' })
      .then((data) => {
        setNotes(Array.isArray(data?.notes) ? data.notes : []);
        setLoadState('ready');
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err.message);
        setLoadState('error');
      });
  }, [cycleCode, planningEntity]);

  useEffect(() => { load(); }, [load]);

  // In compact (per-GL-group) mode, prefill new notes with the active group and show
  // the group's own notes plus entity-wide ones.
  useEffect(() => {
    if (compact) setDraft((previous) => ({ ...previous, glGroup }));
  }, [glGroup, compact]);

  const visibleNotes = useMemo(() => {
    if (!compact || !glGroup) return notes;
    return notes.filter((note) => !note.glGroup || note.glGroup === glGroup);
  }, [notes, compact, glGroup]);

  // Pull the actual GL rollup hierarchy (major rollup → GL line) for this entity so
  // the GL-group tag is a picker over real options instead of free text.
  useEffect(() => {
    if (!planningEntity) { setGlOptions([]); return undefined; }
    let stale = false;
    apiService
      .fetchForecastComparison({ cycleCode, level: 'entity', entity: planningEntity, grain: 'monthly', detail: 'glGroup' })
      .then((data) => {
        if (stale) return;
        const seen = new Set();
        const opts = [];
        (data?.groups || []).forEach((group) => {
          if (!group.glGroup) return;
          const key = `${group.rollup}|${group.glGroup}`;
          if (seen.has(key)) return;
          seen.add(key);
          opts.push({ rollup: group.rollup || 'Other balances', glGroup: group.glGroup });
        });
        setGlOptions(opts);
      })
      .catch(() => { if (!stale) setGlOptions([]); });
    return () => { stale = true; };
  }, [cycleCode, planningEntity]);

  // Group the GL options under their major rollup, ordering the income statement
  // ahead of the balance sheet to mirror the rest of the module.
  const glOptionGroups = useMemo(() => {
    const ROLLUP_ORDER = [
      'Interest Income', 'Interest Expense', 'Non-Interest Income', 'Non-Interest Expense',
      'Provision for Credit Losses', 'Tax Expense', 'Balance Sheet'
    ];
    const byRollup = {};
    glOptions.forEach((option) => {
      (byRollup[option.rollup] = byRollup[option.rollup] || []).push(option.glGroup);
    });
    return Object.keys(byRollup)
      .sort((a, b) => {
        const ia = ROLLUP_ORDER.indexOf(a);
        const ib = ROLLUP_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
      })
      .map((rollup) => ({ rollup, glGroups: byRollup[rollup].sort((a, b) => a.localeCompare(b)) }));
  }, [glOptions]);

  const resetForm = () => {
    setDraft({ ...EMPTY_DRAFT, glGroup: compact ? glGroup : '' });
    setAdding(false);
    setEditingId(null);
  };

  const startEdit = (note) => {
    setEditingId(note.noteGroupId);
    setAdding(true);
    setDraft({
      body: note.body || '',
      horizon: note.horizon || 'durable',
      glGroup: note.glGroup || '',
      expenseType: note.expenseType || '',
      category: note.category || ''
    });
  };

  const save = async () => {
    if (!draft.body.trim() || !planningEntity || disabled) return;
    setBusy(true);
    const payload = {
      author,
      actingAs: author,
      cycleCode,
      planningEntity,
      division,
      market,
      body: draft.body.trim(),
      horizon: draft.horizon,
      glGroup: draft.glGroup.trim() || null,
      expenseType: draft.expenseType || null,
      category: draft.category.trim() || null
    };
    try {
      if (editingId) {
        await apiService.updateContextNote(editingId, payload);
      } else {
        await apiService.createContextNote(payload);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const archive = async (note) => {
    if (disabled) return;
    setBusy(true);
    try {
      await apiService.archiveContextNote(note.noteGroupId, { author });
      load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!planningEntity) {
    return compact ? null : (
      <div className="context-notes-empty forecast-submit-note">
        Open an entity package to record business context for the AI.
      </div>
    );
  }

  return (
    <div className={`context-notes${compact ? ' compact' : ''}`}>
      {!compact && (
        <div className="forecast-panel-head">
          <div>
            <span className="forecast-section-code">OP_CTX · AI business context</span>
            <h3 className="context-notes-title">
              <FolderBrainIcon className="context-brain-icon" width={27} height={27} aria-hidden="true" />
              {title || 'Tell the AI what it needs to know'}
            </h3>
          </div>
        </div>
      )}
      <p className="forecast-submit-note context-notes-sub">
        {subtitle ||
          (compact
            ? `Notes the AI reads when forecasting ${glGroup || 'this line'} — one-time items, seasonality, or how to forecast it.`
            : 'Durable guidance ("this fee dips in Q1 — use a trailing-4-quarter average") and one-time facts ("a $400k legal settlement hits next month"). The AI loads and cites these before every auto-forecast.')}
      </p>

      {error && <p className="forecast-submit-note context-notes-error">{String(error)}</p>}

      <div className="context-notes-list">
        {visibleNotes.length === 0 && loadState === 'ready' && (
          <div className="context-notes-none">No context recorded yet.</div>
        )}
        {visibleNotes.map((note) => (
          <div className="context-note-row" key={note.id}>
            <div className="context-note-body">
              <p>{note.body}</p>
              <div className="context-note-chips">
                <span className={`forecast-health-chip ${note.horizon === 'durable' ? 'moss' : 'amber'}`}>
                  {note.horizon}
                </span>
                {note.glGroup && <span className="forecast-health-chip neutral">GL: {note.glGroup}</span>}
                {note.expenseType && <span className="forecast-health-chip neutral">{note.expenseType}</span>}
                {note.category && <span className="forecast-health-chip neutral">{note.category}</span>}
                <span className="context-note-meta">
                  {note.author}{note.version > 1 ? ` · v${note.version}` : ''} · {formatWhen(note.createdAt)}
                </span>
              </div>
            </div>
            {!disabled && (
              <div className="context-note-actions">
                <button type="button" className="forecast-icon-button" title="Edit (new version)" onClick={() => startEdit(note)}>
                  <Pencil size={13} />
                </button>
                <button type="button" className="forecast-icon-button" title="Remove" onClick={() => archive(note)} disabled={busy}>
                  <Trash2 size={13} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {!disabled && !adding && (
        <button type="button" className="forecast-secondary-button compact context-notes-add" onClick={() => setAdding(true)}>
          <Plus size={13} /> Add context for the AI
        </button>
      )}

      {!disabled && adding && (
        <div className="context-note-form">
          <textarea
            className="context-note-textarea"
            rows={compact ? 2 : 3}
            placeholder="e.g. A one-time $400k vendor true-up hits FY27 — exclude it from the run-rate."
            value={draft.body}
            onChange={(event) => setDraft((previous) => ({ ...previous, body: event.target.value }))}
          />
          <div className="context-note-form-row">
            <div className="forecast-segmented" role="group" aria-label="Horizon">
              <button
                type="button" className={draft.horizon === 'durable' ? 'active' : ''}
                onClick={() => setDraft((previous) => ({ ...previous, horizon: 'durable' }))}
              >
                Durable
              </button>
              <button
                type="button" className={draft.horizon === 'ephemeral' ? 'active' : ''}
                onClick={() => setDraft((previous) => ({ ...previous, horizon: 'ephemeral' }))}
              >
                Ephemeral
              </button>
            </div>
            {glOptionGroups.length > 0 ? (
              <select
                className="context-note-input"
                aria-label="GL group"
                value={draft.glGroup}
                onChange={(event) => setDraft((previous) => ({ ...previous, glGroup: event.target.value }))}
              >
                <option value="">Entity-wide — no specific GL line</option>
                {glOptionGroups.map((group) => (
                  <optgroup key={group.rollup} label={group.rollup}>
                    {group.glGroups.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </optgroup>
                ))}
                {draft.glGroup && !glOptions.some((option) => option.glGroup === draft.glGroup) && (
                  <option value={draft.glGroup}>{draft.glGroup}</option>
                )}
              </select>
            ) : (
              <input
                type="text" className="context-note-input" placeholder="GL group (optional)"
                value={draft.glGroup}
                onChange={(event) => setDraft((previous) => ({ ...previous, glGroup: event.target.value }))}
              />
            )}
            <select
              className="context-note-input"
              value={draft.expenseType}
              onChange={(event) => setDraft((previous) => ({ ...previous, expenseType: event.target.value }))}
            >
              {EXPENSE_TYPES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <input
              type="text" className="context-note-input" placeholder="Category tag — your own label, e.g. Hiring, Litigation (optional)"
              value={draft.category}
              onChange={(event) => setDraft((previous) => ({ ...previous, category: event.target.value }))}
            />
          </div>
          <div className="context-note-form-actions">
            <button type="button" className="forecast-primary-button compact" onClick={save} disabled={busy || !draft.body.trim()}>
              <Check size={13} /> {editingId ? 'Save new version' : 'Save'}
            </button>
            <button type="button" className="forecast-secondary-button compact" onClick={resetForm} disabled={busy}>
              <X size={13} /> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastContextNotes;
